'use client';

import { useEffect } from 'react';
import { track } from '@/utils/track';
import { SW_CDN_OK_MSG, SW_CDN_DOH_SUCCESS_MSG, SW_CDN_DOH_FAIL_MSG, SW_CDN_RETRY_FAIL_MSG, SW_CDN_SWITCH_SUCCESS_MSG, SW_CDN_SWITCHBACK_SUCCESS_MSG, SW_CDN_ALL_FAIL_MSG, SW_CDN_FLUSH_MSG, SW_CDN_OK_DOMAIN_SYNC_MSG, CDN_LAST_OK_KEY, CDN_LAST_OK_TTL, CDN_FAILOVER_SESSION_TOKEN_KEY } from '@/libs/cdn-sw/config';
import { cdnIdbSet, failoverConfirmedIdbClear } from '@/libs/cdn-sw/idb';

/**
 * 将 localStorage 中记录的可用 CDN 域名同步到 SW
 * SW 激活后无法自行读取 localStorage，由此组件 postMessage 过去
 */
function syncOkDomainToSW(sw: ServiceWorker): void {
  try {
    const raw = localStorage.getItem(CDN_LAST_OK_KEY);
    if (!raw) return;
    const rec = JSON.parse(raw) as { domain: string; ts: number; };
    if (rec?.domain && Date.now() - rec.ts < CDN_LAST_OK_TTL) {
      sw.postMessage({ type: SW_CDN_OK_DOMAIN_SYNC_MSG, domain: rec.domain });
    }
  } catch {
    // localStorage 不可用或数据损坏，忽略
  }
}

/**
 * 将 CDN 成功域名写入 localStorage
 */
function saveOkDomain(domain: string): void {
  const record = { domain, ts: Date.now() };
  try {
    localStorage.setItem(CDN_LAST_OK_KEY, JSON.stringify(record));
  } catch {
    // localStorage 不可用，忽略
  }
  // 双写 IDB，供 SW activate 阶段读取（解决 SW 早于主线程 postMessage 的时序问题）
  cdnIdbSet(record);
}

function getNetworkState(): string {
  try {
    return (navigator as unknown as { connection?: { effectiveType?: string; }; }).connection?.effectiveType ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

function pickDohSuccessPayload(data: Record<string, unknown>) {
  return {
    doh_provider: data.doh_provider,
    query_duration_ms: data.query_duration_ms,
    domains_count: data.domains_count,
    network_state: getNetworkState(),
  };
}

function pickDohFailPayload(data: Record<string, unknown>) {
  return {
    doh_provider: data.doh_provider,
    error_message: data.error_message,
    fallback_to_provider: data.fallback_to_provider,
    network_state: getNetworkState(),
  };
}

function pickRetryFailPayload(data: Record<string, unknown>) {
  return {
    domain: data.domain,
    fail_reason: data.fail_reason,
    http_status: data.http_status,
    error_message: data.error_message,
    interception_layer: data.interception_layer,
  };
}

function pickSwitchSuccessPayload(data: Record<string, unknown>) {
  return {
    original_domain: data.original_domain,
    new_domain: data.new_domain,
    fail_reason: data.fail_reason,
    interception_layer: data.interception_layer,
  };
}

function pickSwitchbackSuccessPayload(data: Record<string, unknown>) {
  return {
    interception_layer: data.interception_layer,
    original_domain: data.original_domain,
    new_domain: data.new_domain,
    preferred_domain: data.preferred_domain,
  };
}

function pickAllFailPayload(data: Record<string, unknown>) {
  return {
    domain_list: data.domain_list,
    error_message: data.error_message,
    interception_layer: data.interception_layer,
  };
}

const SW_REG_SESSION_KEY = 'sw_register_session';

/**
 * 监听 Service Worker 的 CDN 相关 postMessage：
 * - CDN_DOH_SUCCESS / CDN_DOH_FAIL：DoH 查询结果上报
 * - CDN_RETRY_FAIL：单次域名重试失败上报
 * - CDN_SWITCH_SUCCESS：域名切换成功上报
 * - CDN_ALL_FAIL：所有域名全部失败上报
 * - CDN_OK：成功事件，写 localStorage 记录可用域名
 *
 * 同时在 SW 激活后，将 localStorage 中已有记录同步给 SW，
 * 使 SW 在本轮 session 优先使用上次可用的域名。
 *
 * 挂载到根 Layout，渲染为空节点
 */
export function CdnFailureReporter() {
  useEffect(() => {
    if (!navigator.serviceWorker) {
      if (!sessionStorage.getItem(SW_REG_SESSION_KEY)) {
        sessionStorage.setItem(SW_REG_SESSION_KEY, '1');
        track('sw_register_fail', { fail_reason: 'not_supported' });
      }
      return;
    }
    (window as typeof window & { __CDN_REACT_REPORTER_READY__?: boolean; }).__CDN_REACT_REPORTER_READY__ = true;

    // 无 sessionStorage token 说明是新 tab（关闭后重开），清除上一个 session 遗留的容灾确认状态。
    // 有 token 说明是刷新，保留 IDB 中的容灾确认状态，使 SW reload 后不需要重新触发判断。
    if (!sessionStorage.getItem(CDN_FAILOVER_SESSION_TOKEN_KEY)) {
      void failoverConfirmedIdbClear();
    }
    sessionStorage.setItem(CDN_FAILOVER_SESSION_TOKEN_KEY, '1');

    const handler = (event: MessageEvent) => {
      const data = event.data ?? {};
      const { type } = data;

      if (type === SW_CDN_DOH_SUCCESS_MSG) {
        track('cdn_doh_success', pickDohSuccessPayload(data));
        return;
      }

      if (type === SW_CDN_DOH_FAIL_MSG) {
        track('cdn_doh_fail', pickDohFailPayload(data));
        return;
      }

      if (type === SW_CDN_RETRY_FAIL_MSG) {
        track('cdn_domain_retry_fail', pickRetryFailPayload(data));
        return;
      }

      if (type === SW_CDN_SWITCH_SUCCESS_MSG) {
        track('cdn_domain_switch_success', pickSwitchSuccessPayload(data));
        return;
      }

      if (type === SW_CDN_SWITCHBACK_SUCCESS_MSG) {
        track('cdn_domain_switchback_success', pickSwitchbackSuccessPayload(data));
        // CDN 首选域名已恢复，清除容灾确认状态，下次 SW 启动不再跳过容灾判断阶段
        void failoverConfirmedIdbClear();
        sessionStorage.removeItem(CDN_FAILOVER_SESSION_TOKEN_KEY);
        return;
      }

      if (type === SW_CDN_ALL_FAIL_MSG) {
        track('cdn_domain_all_fail', pickAllFailPayload(data));
        return;
      }

      if (type === SW_CDN_OK_MSG && typeof data.domain === 'string') {
        saveOkDomain(data.domain);
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);

    // 通知 SW 主线程已就绪，flush SW 侧缓存的上报事件
    navigator.serviceWorker.controller?.postMessage({ type: SW_CDN_FLUSH_MSG });

    // SW 已经是 active 状态时（页面刷新），立即同步
    if (navigator.serviceWorker.controller) {
      syncOkDomainToSW(navigator.serviceWorker.controller);
    }

    // SW 首次激活时同步（首次访问，SW 刚接管）
    const onControllerChange = () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: SW_CDN_FLUSH_MSG });
        syncOkDomainToSW(navigator.serviceWorker.controller);
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // SW 注册成功/失败埋点（仅生产环境，每 session 上报一次）
    if (!sessionStorage.getItem(SW_REG_SESSION_KEY)) {
      navigator.serviceWorker.ready.then(() => {
        if (!sessionStorage.getItem(SW_REG_SESSION_KEY)) {
          sessionStorage.setItem(SW_REG_SESSION_KEY, '1');
          track('sw_register_success');
        }
      });
      navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
        if (!sessionStorage.getItem(SW_REG_SESSION_KEY)) {
          sessionStorage.setItem(SW_REG_SESSION_KEY, '1');
          track('sw_register_fail', {
            fail_reason: 'register_error',
            error_message: error instanceof Error ? error.message : String(error),
          });
        }
      });
    }

    return () => {
      delete (window as typeof window & { __CDN_REACT_REPORTER_READY__?: boolean; }).__CDN_REACT_REPORTER_READY__;
      navigator.serviceWorker.removeEventListener('message', handler);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  return null;
}
